import React, { useMemo } from 'react';
import { MoonStars } from '@phosphor-icons/react';
import { useI18n } from '../../contexts/I18nContext';
import { fmtInTz, zonedParts } from '../../lib/tz';
import { MV, HATCH, toSegments, isNightAt } from '../../lib/movementView';

const FILL = { moving: MV.moving.bar, rest: MV.rest.bar };
const LABEL = { moving: 'mvd_moving', rest: 'mvd_resting', gap: 'mvd_nosignal' };

export function DayBarLegend({ night = true }) {
  const { t } = useI18n();
  const item = (style, key) => (
    <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={style} />{t(key)}</span>
  );
  return (
    <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11px] text-ink-soft" data-testid="day-bar-legend">
      {item({ background: MV.moving.bar }, 'mvd_moving')}
      {item({ background: MV.rest.bar }, 'mvd_resting')}
      {item({ background: HATCH }, 'mvd_nosignal')}
      {night && <span className="inline-flex items-center gap-1.5"><MoonStars size={13} weight="fill" className="text-[#1E293B]" />{t('mvd_night')}</span>}
    </div>
  );
}

// 24 h of 15-min blocks: green moving, pale resting, grey stripes no signal; night underlined in navy.
export function DayBar({ buckets, ids, policy, compact = false, testid = 'day-bar' }) {
  const { t, lang } = useI18n();
  const tz = policy?.timezone || 'UTC';
  const segs = useMemo(() => toSegments(buckets || [], ids), [buckets, ids]);
  const hm = (ms) => fmtInTz(ms, tz, { hour: '2-digit', minute: '2-digit' }, lang);
  const n = segs.length;

  return (
    <div data-testid={testid}>
      <div className={`flex w-full ${compact ? 'h-2.5' : 'h-9'} rounded-md overflow-hidden`}>
        {segs.map((s) => (
          <div key={s.ts} className="flex-1 min-w-0" style={{ background: s.kind === 'gap' ? HATCH : FILL[s.kind] }}
            title={`${hm(s.ts)}–${hm(s.ts + s.segMin * 60000)} · ${t(LABEL[s.kind])}`} data-kind={s.kind} />
        ))}
      </div>
      {!compact && policy && (
        <div className="flex w-full h-1.5 mt-1" aria-hidden>
          {segs.map((s) => <div key={s.ts} className="flex-1" style={{ background: isNightAt(s.ts, policy) ? '#1E293B' : 'transparent', opacity: 0.55 }} />)}
        </div>
      )}
      {!compact && (
        <div className="relative h-4 mt-1 text-[10px] text-ink-muted font-mono">
          {segs.map((s, i) => {
            const p = zonedParts(s.ts, tz);
            if (p.minutes % 360 !== 0 || i > n - 8) return null;
            return <span key={s.ts} className="absolute -translate-x-1/2" style={{ left: `${(i / n) * 100}%` }}>{hm(s.ts)}</span>;
          })}
          <span className="absolute right-0 font-sans font-semibold text-ink-soft">{t('mvd_now')}</span>
        </div>
      )}
    </div>
  );
}
